import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import Card, { CardHeader, CardTitle, CardContent } from '../UI/Card'
import Button from '../UI/Button'
import LoadingSpinner from '../UI/LoadingSpinner'
import SSOConfigurationModal from '../Modals/SSOConfigurationModal'
import ConfirmationModal from '../Modals/ConfirmationModal'
import { Shield, Plus, Edit, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'
import api from '../../services/api'
import { useAuth } from '../../hooks/useAuth'

export default function SSOConfigurationTab({ organizationId }) {
  const { hasPermission } = useAuth()
  const [showConfigModal, setShowConfigModal] = useState(false)
  const [selectedConfig, setSelectedConfig] = useState(null)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [configToDelete, setConfigToDelete] = useState(null)
  const queryClient = useQueryClient()

  // Fetch SSO configurations
  const { data: configsData, isLoading, refetch } = useQuery(
    ['sso-configurations', organizationId],
    () => api.get(`/sso-configurations/organization/${organizationId}`).then(r => r.data),
    { enabled: !!organizationId }
  )
  const configurations = configsData?.data?.configurations || []

  // Test connection mutation
  const testConnectionMutation = useMutation(
    (configId) => api.post(`/sso-configurations/${configId}/test`).then(r => r.data),
    {
      onSuccess: (result) => {
        if (result?.success) {
          toast.success('Connection test successful!')
        } else {
          toast.error('Connection test failed')
        }
        refetch()
      },
      onError: () => {
        toast.error('Connection test failed')
      }
    }
  )

  // Delete configuration mutation
  const deleteMutation = useMutation(
    (configId) => api.delete(`/sso-configurations/${configId}`).then(r => r.data),
    {
      onSuccess: (result) => {
        if (result?.success) {
          toast.success('SSO configuration deleted successfully!')
          queryClient.invalidateQueries(['sso-configurations', organizationId])
        } else {
          toast.error(result?.message || 'Failed to delete SSO configuration')
        }
      },
      onError: () => {
        toast.error('Failed to delete SSO configuration')
      }
    }
  )

  // Toggle status mutation
  const toggleStatusMutation = useMutation(
    ({ configId, isActive }) => api.patch(`/sso-configurations/${configId}/toggle-status`, { is_active: isActive }).then(r => r.data),
    {
      onSuccess: (result) => {
        if (result?.success) {
          toast.success(result?.message || `SSO provider ${result?.data?.configuration?.is_active ? 'enabled' : 'disabled'} successfully`)
          queryClient.invalidateQueries(['sso-configurations', organizationId])
        } else {
          toast.error(result?.message || 'Failed to toggle SSO configuration status')
        }
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to toggle SSO configuration status')
      }
    }
  )

  const handleCreateConfig = () => {
    setSelectedConfig(null)
    setShowConfigModal(true)
  }

  const handleEditConfig = (config) => {
    setSelectedConfig(config)
    setShowConfigModal(true)
  }

  const handleTestConnection = (configId) => {
    testConnectionMutation.mutate(configId)
  }

  const handleDeleteConfig = (config) => {
    setConfigToDelete(config)
    setShowDeleteConfirmation(true)
  }

  const confirmDeleteConfig = () => {
    if (configToDelete) {
      deleteMutation.mutate(configToDelete.id)
      setShowDeleteConfirmation(false)
      setConfigToDelete(null)
    }
  }

  const cancelDeleteConfig = () => {
    setShowDeleteConfirmation(false)
    setConfigToDelete(null)
  }

  const handleConfigSuccess = () => {
    setShowConfigModal(false)
    setSelectedConfig(null)
    queryClient.invalidateQueries(['sso-configurations', organizationId])
    queryClient.invalidateQueries(['organization', organizationId])
  }


  // Check if user has permission to view organization data
  if (!hasPermission('organizations.read')) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
          <p className="text-gray-600">You don't have permission to view SSO configurations.</p>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-secondary-900">SSO Configuration</h3>
          <p className="text-sm text-secondary-600">
            Configure Single Sign-On for this organization. Email/password is always created for product app login.
          </p>
        </div>
        {hasPermission('organizations.create') && (
          <Button onClick={handleCreateConfig}>
            <Plus className="h-4 w-4 mr-2" />
            Add SSO Configuration
          </Button>
        )}
      </div>

      {/* Configurations Table */}
      {configurations.length > 0 ? (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Provider
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Client ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {configurations.map((config) => {
                    // Format provider name
                    const getProviderName = (provider) => {
                      const providerMap = {
                        'email_password': 'Email_password',
                        'saml': 'SAML 2.0',
                        'oidc': 'OpenID Connect',
                        'google': 'Google',
                        'microsoft': 'Microsoft',
                        'azure': 'Azure AD',
                        'okta': 'Okta',
                        'auth0': 'Auth0',
                        'onelogin': 'OneLogin',
                        'pingidentity': 'Ping Identity',
                        'adfs': 'ADFS',
                        'keycloak': 'Keycloak',
                        'jumpcloud': 'JumpCloud',
                        'duo': 'Duo',
                        'sailpoint': 'SailPoint'
                      };
                      return providerMap[provider] || provider;
                    };

                    // Format date
                    const formatDate = (dateString) => {
                      if (!dateString) return 'N/A';
                      const date = new Date(dateString);
                      return date.toLocaleDateString('en-US', {
                        month: '2-digit',
                        day: '2-digit',
                        year: 'numeric'
                      });
                    };

                    // Get client ID display
                    const getClientId = () => {
                      if (config.provider === 'email_password') {
                        return 'N/A';
                      }
                      return config.client_id || 'N/A';
                    };

                    return (
                      <tr key={config.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-medium text-gray-900">
                            {getProviderName(config.provider)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-600">
                            {getClientId()}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <button
                              type="button"
                              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                config.is_active ? 'bg-green-500' : 'bg-gray-200'
                              } ${toggleStatusMutation.isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                              role="switch"
                              aria-checked={config.is_active}
                              disabled={toggleStatusMutation.isLoading}
                              onClick={() => {
                                const newStatus = !config.is_active
                                toggleStatusMutation.mutate({ configId: config.id, isActive: newStatus })
                              }}
                            >
                              <span
                                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                  config.is_active ? 'translate-x-5' : 'translate-x-0'
                                }`}
                              />
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-600">
                            {formatDate(config.created_at)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            {hasPermission('organizations.update') && (
                              <button
                                onClick={() => handleEditConfig(config)}
                                className="text-gray-600 hover:text-gray-900 p-1"
                                title="Edit"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                            )}
                            {hasPermission('organizations.delete') && (
                              <button
                                onClick={() => handleDeleteConfig(config)}
                                disabled={deleteMutation.isLoading}
                                className="text-red-600 hover:text-red-900 p-1 disabled:opacity-50"
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="text-center py-12">
            <Shield className="h-12 w-12 text-secondary-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-secondary-900 mb-2">No SSO Configuration</h3>
            <p className="text-secondary-600 mb-4">
              Configure Single Sign-On to allow users to authenticate with external providers.
            </p>
            {hasPermission('organizations.update') && (
              <Button onClick={handleCreateConfig}>
                <Shield className="h-4 w-4 mr-2" />
                Configure SSO
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* SSO Configuration Modal */}
      <SSOConfigurationModal
        isOpen={showConfigModal}
        onClose={() => {
          setShowConfigModal(false)
          setSelectedConfig(null)
        }}
        organizationId={organizationId}
        ssoConfig={selectedConfig}
        onSuccess={handleConfigSuccess}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={cancelDeleteConfig}
        onConfirm={confirmDeleteConfig}
        title="Delete SSO Configuration"
        message={`Are you sure you want to delete this SSO configuration? This action cannot be undone and will remove all SSO settings for this organization.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}


